import React, { useState } from 'react';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
    getSteps,
    exampleEmployees,
    exampleGeneralInfo,
    exampleSchedules,
    exampleStaffRequirements,
    exampleStaffDistribution,
    exampleShiftSequence,
    exampleWeeklySum
} from '../../components/utils/exampleUtils';

const ExamplePage = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(0);
    const { t } = useTranslation(); 

    const steps = getSteps(t);

    const handleTryExample = () => {
        sessionStorage.clear();
        
        sessionStorage.setItem('employees', JSON.stringify(exampleEmployees));
        sessionStorage.setItem('general', JSON.stringify(exampleGeneralInfo));
        sessionStorage.setItem('schedules', JSON.stringify(exampleSchedules));
        sessionStorage.setItem('staffRequirements', JSON.stringify(exampleStaffRequirements));
        sessionStorage.setItem('distributions', JSON.stringify(exampleStaffDistribution));
        sessionStorage.setItem('shiftSequenceParams', JSON.stringify(exampleShiftSequence));
        sessionStorage.setItem('weeklySumParams', JSON.stringify(exampleWeeklySum));

        navigate('/scenarios', {
            state: {isExample: true}
        });
    };

    return (
        <div className='min-h-screen bg-gray-100 -mt-16 pt-15'>
          <div className='bg-gray-50'>
            <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-24'>
              <div className='text-center'>
                <h1 className='text-2xl md:text-4xl font-extrabold text-gray-900'>
                  {t('example.title')}
                </h1>
                <p className='mt-3 max-w-md mx-auto text-sm md:text-base text-gray-500 md:mt-5 md:max-w-3xl'>
                  {t('example.subtitle')}
                </p>
                <div className='mt-6 md:mt-10'>
                  <button
                    onClick={handleTryExample}
                    className='inline-flex items-center px-6 py-3 md:px-8 md:py-4 text-sm md:text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700'
                  >
                    {t('example.tryButton')}
                    <ArrowRight className='ml-2 md:ml-3 h-4 w-4 md:h-5 md:w-5'/>
                  </button>
                </div>
              </div>
            </div>
          </div>
    
          <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16'>
            <div className='text-center mb-8 md:mb-16'>
              <h2 className='text-xl md:text-3xl font-bold text-gray-900'>{t('example.howItWorks.title')}</h2>
              <p className='mt-2 md:mt-4 text-sm md:text-lg text-gray-600'>
                {t('example.howItWorks.subtitle')}
              </p>
            </div>
    
            <div className='space-y-4 md:space-y-8'>
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`relative bg-white p-4 md:p-8 rounded-lg shadow-sm border ${
                    currentStep === index ? 'border-indigo-600' : 'border-gray-200'
                  }`}
                >
                  <div className='flex items-center gap-4 md:gap-6'>
                    <div className='flex-shrink-0'>
                      <div className='flex items-center justify-center w-8 h-8 md:w-12 md:h-12 rounded-md bg-indigo-600 text-white'>
                        <step.icon className='h-4 w-4 md:h-6 md:w-6' />
                      </div>
                    </div>
                    <div className='flex-1'>
                      <div className='flex justify-between items-center mb-1 md:mb-2'>
                        <h3 className='text-sm md:text-xl font-semibold text-gray-900'>
                          {t('example.step')} {index + 1}: {step.title}
                        </h3>
                        <ChevronRight className='h-4 w-4 md:h-5 md:w-5 text-gray-400'/>
                      </div>
                      <p className='text-xs md:text-base text-gray-600 mb-2 md:mb-4'>{step.description}</p>
                      <div className='bg-gray-50 p-2 md:p-4 rounded-md'>
                        <p className='text-xs md:text-sm text-gray-600'>
                          <span className='font-medium'>{t('example.exampleLabel')}: </span>
                          {step.example}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
           
            <div className='text-center mt-8 md:mt-12'>
              <button
                onClick={handleTryExample}
                className='inline-flex items-center px-6 py-3 md:px-8 md:py-4 text-sm md:text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700'
              >
                {t('example.getStarted')}
                <ArrowRight className='ml-2 md:ml-3 h-4 w-4 md:h-5 md:w-5'/>
              </button>
            </div>
          </div>
        </div>
      );
};

export default ExamplePage;